import { z } from 'zod';
import { ConditionOperator, ConditionsOperand, ConditionsType, FieldPermission as FieldPermissionEnum, LEGISLATIVE_ITEM_WORKFLOW_DYNAMIC_ASSIGNMENT_TYPES, LegislativeItemWorkflowAssignmentType, LegislativeItemWorkflowStepType, RejectionPolicyType, } from '@govinity/shared-config';
import { initiallyNullNumber } from '../shared/index.js';
export * from '@govinity/shared-config/legislative-item-workflows';
const workflowConditionSchema = z.object({
    field: z.string('Field name is required').min(1, 'Field name is required'), // TODO: define allowed field names
    operator: z.enum(ConditionOperator, 'Operator is required'),
    value: z.union([z.string('Value is required').min(1, 'Value is required'), z.number(), z.boolean()], 'Value is required'),
});
// ============================================================================
// Field Permission Schema
// ============================================================================
const fieldPermissionSchema = z.object({
    field: z.string('Field name is required').min(1, 'Field name is required'), // TODO: define allowed field names
    permission: z.enum(FieldPermissionEnum),
});
// ============================================================================
// Required Document Schema
// ============================================================================
const requiredDocumentSchema = z.object({
    documentTypeId: z.number('Document ID is required').int().positive(),
    name: z.string('Document name is required').min(1, 'Document name is required'),
});
// ============================================================================
// Deadline Action Schema
// ============================================================================
const assignmentTypesRequiringId = [
    LegislativeItemWorkflowAssignmentType.User,
    LegislativeItemWorkflowAssignmentType.EntityPosition,
    LegislativeItemWorkflowAssignmentType.DepartmentPosition,
    LegislativeItemWorkflowAssignmentType.ParentDepartmentPosition,
];
const deadlineNotificationSchema = z.object({
    type: z.enum(assignmentTypesRequiringId),
    id: z.number().int().positive(),
});
const deadlineActionSchema = z.object({
    type: z.enum(['notify', 'advance']).optional(),
    notifications: z.array(deadlineNotificationSchema).nullish(),
});
// ============================================================================
// Rejection Policy Schema
// ============================================================================
const rejectionPolicySchema = z
    .object({
    value: z.enum(RejectionPolicyType),
    stepId: z.number().int().positive().optional(),
})
    .refine((data) => {
    if (data.value === RejectionPolicyType.ToStep) {
        return typeof data.stepId === 'number' && data.stepId > 0;
    }
    return true;
}, {
    message: 'Step ID is required when setting rejection policy to a specific step',
    path: ['stepId'],
});
// ============================================================================
// Workflow Template Schemas
// ============================================================================
export const legislativeItemWorkflowTemplateCreateSchema = z.object({
    name: z.string('Workflow name is required').min(1, 'Workflow name is required'),
});
/**
 * Schema for updating a workflow template.
 */
export const legislativeItemWorkflowTemplateUpdateSchema = legislativeItemWorkflowTemplateCreateSchema.partial();
/**
 * Assignment schema with discriminated union:
 * - User, EntityPosition, DepartmentPosition, ParentDepartmentPosition require an `id`
 * - Creator and StaffContact do not require an `id` (they resolve to the item's
 *   creator / staff contact at runtime)
 */
const assignmentWithIdSchema = z.object({
    type: z.enum(assignmentTypesRequiringId),
    id: initiallyNullNumber('Required'),
});
const assignmentWithoutIdSchema = z.object({
    type: z.enum(LEGISLATIVE_ITEM_WORKFLOW_DYNAMIC_ASSIGNMENT_TYPES),
    id: z.number().int().positive().nullable().optional(),
});
export const stepAssignmentSchema = z.union([assignmentWithIdSchema, assignmentWithoutIdSchema]);
// ============================================================================
// Workflow Template Step Schemas
// ============================================================================
const conditionsTypeValues = Object.values(ConditionsType);
const conditionsOperandValues = Object.values(ConditionsOperand);
export const templateStepBaseSchema = z.object({
    name: z.string('Step name is required').min(1, 'Step name is required'),
    orderById: z.number().int().optional(),
    subOrderById: z.number().int().optional(),
    deadlineDays: z.number().int().positive().nullish(),
    minApprovals: z.number('Number is required').int().positive().default(1),
    stepType: z.enum(LegislativeItemWorkflowStepType, 'Step type is required'),
    assignments: z.array(stepAssignmentSchema).default([]),
    conditions: z.array(workflowConditionSchema).default([]),
    conditionsType: z.enum(conditionsTypeValues).default(ConditionsType.Skip),
    conditionsOperand: z.enum(conditionsOperandValues).default(ConditionsOperand.Some),
    fieldPermissions: z.array(fieldPermissionSchema).default([]),
    requiredDocuments: z.array(requiredDocumentSchema).default([]),
    deadlineAction: deadlineActionSchema.default({}),
    rejectionPolicies: z.array(rejectionPolicySchema).default([]),
    shouldExportApproval: z.boolean().optional(),
});
export const templateStepSuperRefine = (data, ctx) => {
    // Document create steps require at least one required document
    // Only validate if stepType is explicitly provided (for partial updates)
    if (data.stepType === LegislativeItemWorkflowStepType.DocumentCreate &&
        data.requiredDocuments !== undefined &&
        data.requiredDocuments.length === 0) {
        ctx.addIssue({
            code: 'custom',
            message: 'Document create step type requires at least one required document',
            path: ['requiredDocuments'],
        });
    }
    // Approve/reject steps require at least one rejection policy
    // Only validate if stepType is explicitly provided (for partial updates)
    if (data.stepType === LegislativeItemWorkflowStepType.ApproveReject) {
        const policies = data.rejectionPolicies;
        if (policies === undefined || policies.length === 0) {
            ctx.addIssue({
                code: 'custom',
                message: 'At least one rejection policy is required',
                path: ['rejectionPolicies'],
            });
        }
    }
    // Steps that are not ToDeliberation require at least one assignment
    // Only validate if stepType is explicitly provided (for partial updates)
    if (data.stepType !== undefined &&
        data.stepType !== LegislativeItemWorkflowStepType.ToDeliberation &&
        data.assignments !== undefined &&
        data.assignments.length === 0) {
        ctx.addIssue({
            code: 'custom',
            message: 'At least one assignment is required',
            path: ['assignments'],
        });
    }
};
const normalizeTemplateStepRejectionPolicies = (data) => {
    if (data.stepType !== undefined &&
        data.stepType !== LegislativeItemWorkflowStepType.ApproveReject) {
        return { ...data, rejectionPolicies: [] };
    }
    return data;
};
export const legislativeItemWorkflowTemplateStepCreateSchema = templateStepBaseSchema
    .superRefine(templateStepSuperRefine)
    .transform(normalizeTemplateStepRejectionPolicies);
export const legislativeItemWorkflowTemplateStepUpdateSchema = templateStepBaseSchema
    .partial()
    .superRefine(templateStepSuperRefine)
    .transform(normalizeTemplateStepRejectionPolicies);
export const legislativeItemWorkflowTemplateStepReorderSchema = z.object({
    steps: z
        .array(z.object({
        id: z.number().int().positive(),
        orderById: z.number().int().nonnegative(),
        subOrderById: z.number().int().nonnegative().nullable(),
    }))
        .min(1, 'At least one step is required'),
});
// ============================================================================
// Workflow Action Input Schemas
// ============================================================================
/**
 * Schema for approving a workflow assignment.
 */
export const workflowApproveSchema = z.object({
    note: z.string().max(2000).optional(),
});
/**
 * Schema for rejecting a workflow assignment.
 */
export const workflowRejectSchema = z
    .object({
    rejectionPolicy: z.enum(RejectionPolicyType),
    stepId: z.number().int().positive().optional(),
    note: z.string().max(2000).optional(),
})
    .refine((data) => {
    if (data.rejectionPolicy === RejectionPolicyType.ToStep) {
        return typeof data.stepId === 'number' && data.stepId > 0;
    }
    return true;
}, {
    message: 'Step ID is required when rejection policy is to_step',
    path: ['stepId'],
});
/**
 * Schema for confirming a workflow assignment (confirmation step type).
 */
export const workflowConfirmSchema = z.object({
    note: z.string().max(2000).optional(),
});
/**
 * Schema for cancelling a workflow.
 */
export const workflowCancelSchema = z.object({
    reason: z.string().max(2000).optional(),
});
export const workflowAdvanceFromDeliberationSchema = z.object({
    action: z.enum(['close', 'reject', 'advance']),
});
/**
 * Schema for undoing a kill rejection.
 */
export const workflowUndoSchema = z.object({
    note: z.string().max(2000).optional(),
});
/**
 * Schema for updating workflow step assignments (reassignment).
 */
export const workflowStepAssignmentsUpdateSchema = z.object({
    assignments: z.array(stepAssignmentSchema).min(1, 'At least one assignment is required'),
});
/**
 * Schema for adding a step to a running workflow instance (on the fly).
 *
 * Reuses the template step definition fields but drops `subOrderById` (ad-hoc
 * steps are always their own group) and forbids `to_deliberation` steps, which
 * cannot be introduced into a live workflow.
 *
 * `orderById` is the position the new step should occupy: existing steps at that
 * `orderById` and after are shifted up by one to make room. For example, for
 * steps ordered `1, 2, 3, 4, 5`, passing `orderById: 2` inserts the new step
 * before the current step 2 (which, with the steps after it, becomes `3, 4, 5, 6`).
 */
export const workflowAddStepSchema = templateStepBaseSchema
    .omit({ orderById: true, subOrderById: true })
    .extend({
    orderById: z
        .number('Position is required')
        .int()
        .positive('Position must be a positive number'),
    stepType: z.enum(LegislativeItemWorkflowStepType).exclude(['ToDeliberation'], {
        error: (issue) => issue.input === undefined
            ? 'Step type is required'
            : 'A to deliberation step cannot be added to a running workflow',
    }),
})
    .superRefine(templateStepSuperRefine)
    .transform(normalizeTemplateStepRejectionPolicies);
export var LegislativeItemWorkflowTemplateStatus;
(function (LegislativeItemWorkflowTemplateStatus) {
    LegislativeItemWorkflowTemplateStatus["Draft"] = "draft";
    LegislativeItemWorkflowTemplateStatus["Published"] = "published";
    LegislativeItemWorkflowTemplateStatus["Archived"] = "archived";
})(LegislativeItemWorkflowTemplateStatus || (LegislativeItemWorkflowTemplateStatus = {}));
export const userWorkflowReassignmentSchema = z
    .object({
    fromUserId: z.number().int().positive(),
    toUserId: z.number().int().positive(),
})
    .refine(({ fromUserId, toUserId }) => fromUserId !== toUserId, {
    message: 'The replacement must be a different user',
    path: ['toUserId'],
});
